import {
  CustomerOrderByWithRelationInput,
  CustomersQuery,
  CustomerWhereInput,
  QueryMode,
  SortOrder,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { ActionIcon, Avatar, Box, Group, Paper, Stack, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconCheck, IconEdit, IconEye, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerForm, CustomerInput } from '../components/CustomerForm';
import { deleteCustomerDocument } from '../graphql/delete-customer';
import { countCustomersDocument, customersDocument } from '../graphql/get-customers';
import { upsertCustomerDocument } from '../graphql/upsert-customer';

export const CustomerList = () => {
  const navigate = useNavigate();

  const [filterStatus, setFilterStatus] = useState<CustomerWhereInput>({});
  const [where] = useDebouncedValue(filterStatus, 50);

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<CustomersQuery['customers'][number]>
  >({
    columnAccessor: 'createdAt',
    direction: 'desc',
  });
  const orderBy = useMemo<CustomerOrderByWithRelationInput>(() => {
    switch (sortStatus.columnAccessor) {
      case '_count.orders':
        return { orders: { _count: sortStatus.direction as SortOrder } };
      case '_count.messages':
        return { messages: { _count: sortStatus.direction as SortOrder } };
      case '_count.addresses':
        return { addresses: { _count: sortStatus.direction as SortOrder } };
      case '_count.refunds':
        return { refunds: { _count: sortStatus.direction as SortOrder } };
      default:
        return { [sortStatus.columnAccessor]: sortStatus.direction };
    }
  }, [sortStatus]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: count } = useQuery(countCustomersDocument, { variables: { where } });

  const { data, loading } = useQuery(customersDocument, {
    variables: {
      skip: pageSize * page - pageSize,
      take: pageSize,
      orderBy,
      where,
    },
  });

  const [upsertCustomer, upsertCustomerMutation] = useMutation(upsertCustomerDocument, {
    refetchQueries: [customersDocument],
  });

  const [deleteCustomer, deleteCustomerMutation] = useMutation(deleteCustomerDocument, {
    refetchQueries: [customersDocument],
  });

  const handleUpsert = async ({ id, name, pageId, email, avatar }: CustomerInput) => {
    await upsertCustomer({
      variables: {
        where: { id: id ?? '' },
        create: { name, pageId, email, avatar },
        update: {
          pageId: { set: pageId },
          name: { set: name },
          ...(email && { email: { set: email } }),
          ...(avatar && { avatar: { set: avatar } }),
        },
      },
    });

    modals.closeAll();
  };

  return (
    <Stack>
      <Paper p="md" withBorder component={Group}>
        <Group>
          <TextInput
            placeholder="Name..."
            value={filterStatus.name?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                name: { contains: event.currentTarget.value, mode: QueryMode.Insensitive },
              }));
            }}
          />
          <TextInput
            placeholder="Email..."
            value={filterStatus.email?.contains ?? ''}
            onChange={(event) => {
              setFilterStatus((prev) => ({
                ...prev,
                email: { contains: event.currentTarget.value, mode: QueryMode.Insensitive },
              }));
            }}
          />
        </Group>
      </Paper>
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        textSelectionDisabled
        pinLastColumn
        borderRadius="sm"
        minHeight={250}
        records={data?.customers}
        fetching={loading}
        totalRecords={count?.countCustomers ?? 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[5, 10, 25, 50, 75, 100]}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          {
            accessor: 'name',
            title: 'Name',
            sortable: true,
            render: ({ avatar, name }) => (
              <Group>
                <Avatar src={avatar} size="sm" />
                <Box ml={2}>{name}</Box>
              </Group>
            ),
          },
          {
            accessor: 'createdAt',
            title: 'Created at',
            sortable: true,
            render: ({ createdAt }) => dayjs(createdAt).format('DD MMM YYYY HH:mm'),
          },
          {
            accessor: 'updatedAt',
            title: 'Updated at',
            sortable: true,
            render: ({ updatedAt }) => dayjs(updatedAt).format('DD MMM YYYY HH:mm'),
          },
          {
            accessor: '_count.orders',
            title: 'Orders',
            sortable: true,
          },
          {
            accessor: 'active',
            title: 'Active',
            render: ({ active }) =>
              active ? <IconCheck color="teal" size={16} /> : <IconX color="red" size={16} />,
            sortable: true,
          },
          {
            accessor: 'reason',
            title: 'Reason',
          },
          {
            accessor: '_count.messages',
            title: 'Messages',
            sortable: true,
          },
          {
            accessor: '_count.addresses',
            title: 'Addresses',
            sortable: true,
          },
          {
            accessor: '_count.refunds',
            title: 'Refunds',
            sortable: true,
          },
          {
            accessor: 'actions',
            textAlign: 'right',
            title: (
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() =>
                  modals.open({
                    children: (
                      <CustomerForm
                        onSubmit={(data) => void handleUpsert(data)}
                        loading={upsertCustomerMutation.loading}
                      />
                    ),
                    size: 'xl',
                  })
                }
              >
                <IconPlus size={16} />
              </ActionIcon>
            ),
            render: (customer) => (
              <Group gap={4} justify="right" wrap="nowrap">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={() => void navigate(customer.id)}
                >
                  <IconEye size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="blue"
                  onClick={() =>
                    modals.open({
                      title: 'Edit customer',
                      children: (
                        <CustomerForm
                          customer={customer}
                          onSubmit={(data) => void handleUpsert(data)}
                          loading={upsertCustomerMutation.loading}
                        />
                      ),
                    })
                  }
                >
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  loading={deleteCustomerMutation.loading}
                  onClick={() =>
                    modals.openConfirmModal({
                      title: `Delete Customer ${customer.name}?`,
                      centered: true,
                      labels: { confirm: 'Delete CUSTOMER', cancel: "No don't delete it" },
                      confirmProps: { color: 'red' },
                      onConfirm: () =>
                        void deleteCustomer({ variables: { where: { id: customer.id } } }),
                    })
                  }
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
      />
    </Stack>
  );
};
